import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { SITE_ID, nowTR } from '../../../lib/constants';
import { CartItem } from '../../../contexts/CartContext';
import { useAuth } from '../../../contexts/AuthContext';

export interface AddressFormData {
  ad_soyad: string;
  telefon: string;
  email: string;
  il_kodu: string;
  il_adi: string;
  ilce: string;
  adres: string;
  notlar: string;
}

export interface SavedAddress {
  id: string;
  ad_soyad: string;
  telefon: string;
  adres_basligi: string;
  adres: string;
  il_adi: string;
  il_kodu: string;
  ilce: string;
  varsayilan: boolean;
}

export interface CouponData {
  id: string;
  kampanya_adi: string;
  kampanya_tipi: 'yuzde' | 'yuzde_indirim' | 'tutar' | 'tutar_indirim' | 'ucretsiz_kargo' | 'hediye_urun';
  indirim_degeri: number;
  maksimum_indirim: number | null;
  kupon_kodu: string;
}

export type PaymentMethod = 'card' | 'bank' | 'cash' | null;

function generateSiparisNo(): string {
  const now = new Date();
  const year = now.getFullYear();
  const rand = Math.floor(10000 + Math.random() * 90000);
  return `ZC-${year}-${rand}`;
}

export function useCheckout(
  items: CartItem[],
  totalPrice: number,
  codFeeAmount = 29,
  shippingCostBase = 49.9,
  freeShippingLimit = 500,
  codUpperLimit = 0
) {
  const { user } = useAuth();
  const [step, setStep] = useState<'address' | 'payment' | 'confirm'>('address');
  const [addressData, setAddressData] = useState<AddressFormData>({
    ad_soyad: '', telefon: '', email: '',
    il_kodu: '', il_adi: '', ilce: '', adres: '', notlar: ''
  });
  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);

  // Kullanıcı giriş yapmışsa bilgileri ve kayıtlı adresleri otomatik doldur
  useEffect(() => {
    if (!user) return;

    const fullName = user.user_metadata?.full_name || '';
    const email = user.email || '';

    // Kayıtlı adresleri çek
    supabase
      .from('kullanici_adresleri')
      .select('*')
      .eq('site_id', SITE_ID)
      .eq('user_id', user.id)
      .order('varsayilan', { ascending: false })
      .then(async ({ data: addrs }) => {
        if (addrs && addrs.length > 0) {
          // il_adi → il_kodu eşleştirmesi için tüm illeri çek
          const { data: illerData } = await supabase.from('iller').select('il_kodu, il_adi');
          const ilMap: Record<string, string> = {};
          (illerData || []).forEach((il: { il_kodu: string; il_adi: string }) => {
            ilMap[il.il_adi] = il.il_kodu;
          });

          const mapped: SavedAddress[] = addrs.map((a: Record<string, unknown>) => ({
            id: a.id as string,
            ad_soyad: (a.ad_soyad as string) || '',
            telefon: (a.telefon as string) || '',
            adres_basligi: (a.adres_basligi as string) || 'Adresim',
            adres: (a.adres as string) || '',
            il_adi: (a.il as string) || '',
            il_kodu: ilMap[(a.il as string)] || '',
            ilce: (a.ilce as string) || '',
            varsayilan: (a.varsayilan as boolean) || false,
          }));

          setSavedAddresses(mapped);

          // Varsayılan adresi seç ve forma doldur
          const defaultAddr = mapped.find(a => a.varsayilan) || mapped[0];
          setSelectedAddressId(defaultAddr.id);
          setAddressData({
            ad_soyad: defaultAddr.ad_soyad || fullName,
            telefon: defaultAddr.telefon,
            email,
            il_kodu: defaultAddr.il_kodu,
            il_adi: defaultAddr.il_adi,
            ilce: defaultAddr.ilce,
            adres: defaultAddr.adres,
            notlar: '',
          });
        } else {
          // Kayıtlı adres yoksa sadece isim ve e-posta doldur
          setAddressData(prev => ({ ...prev, ad_soyad: fullName, email }));
        }
      });
  }, [user]);
  const [addressErrors, setAddressErrors] = useState<Partial<AddressFormData>>({});
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(null);
  const [paymentError, setPaymentError] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [coupon, setCoupon] = useState<CouponData | null>(null);
  const [couponError, setCouponError] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderNo, setOrderNo] = useState('');
  const [orderError, setOrderError] = useState('');

  const shippingCost = totalPrice >= freeShippingLimit ? 0 : shippingCostBase;

  const isYuzde = (t: string) => t === 'yuzde' || t === 'yuzde_indirim';
  const isTutar = (t: string) => t === 'tutar' || t === 'tutar_indirim';

  const discount = (() => {
    if (!coupon) return 0;
    if (isYuzde(coupon.kampanya_tipi)) {
      const raw = totalPrice * (coupon.indirim_degeri / 100);
      return coupon.maksimum_indirim ? Math.min(raw, coupon.maksimum_indirim) : raw;
    }
    if (isTutar(coupon.kampanya_tipi)) return Math.min(coupon.indirim_degeri, totalPrice);
    if (coupon.kampanya_tipi === 'ucretsiz_kargo') return shippingCost;
    return 0;
  })();

  const freeShipping = coupon?.kampanya_tipi === 'ucretsiz_kargo' || totalPrice >= freeShippingLimit;
  const effectiveShipping = freeShipping ? 0 : shippingCost;

  // Kapıda ödeme üst limit kontrolü: limit > 0 ise ve sepet tutarı limiti aşıyorsa kapıda ödeme kullanılamaz
  const codLimitExceeded = codUpperLimit > 0 && totalPrice > codUpperLimit;

  const codFee = paymentMethod === 'cash' && !codLimitExceeded ? codFeeAmount : 0;
  const isFreeCargo = coupon?.kampanya_tipi === 'ucretsiz_kargo';
  const grandTotal = totalPrice + effectiveShipping + codFee - (isFreeCargo ? 0 : discount);

  const validateAddress = (): boolean => {
    const errors: Partial<AddressFormData> = {};
    if (!addressData.ad_soyad.trim()) errors.ad_soyad = 'Ad Soyad zorunlu';
    if (!addressData.telefon.trim()) errors.telefon = 'Telefon zorunlu';
    else if (addressData.telefon.replace(/\D/g, '').length !== 10) errors.telefon = 'Geçerli bir telefon numarası girin (5XX XXX XX XX)';
    else if (!/^5/.test(addressData.telefon.replace(/\D/g, ''))) errors.telefon = 'Telefon numarası 5 ile başlamalıdır';
    if (!addressData.email.trim()) errors.email = 'E-posta zorunlu';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addressData.email)) errors.email = 'Geçerli e-posta girin';
    if (!addressData.il_kodu.trim()) errors.il_kodu = 'İl zorunlu';
    if (!addressData.ilce.trim()) errors.ilce = 'İlçe zorunlu';
    if (!addressData.adres.trim()) errors.adres = 'Adres zorunlu';
    setAddressErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError('');
    setCoupon(null);

    const { data, error } = await supabase
      .from('kampanyalar')
      .select('id, kampanya_adi, kampanya_tipi, indirim_degeri, maksimum_indirim, kupon_kodu, minimum_sepet_tutari, bitis_tarihi, kullanim_limiti, kullanim_sayisi')
      .eq('site_id', SITE_ID)
      .eq('kupon_kodu', couponCode.toUpperCase().trim())
      .eq('aktif', true)
      .maybeSingle();

    setCouponLoading(false);

    if (error || !data) {
      setCouponError('Geçersiz kupon kodu');
      return;
    }
    if (data.bitis_tarihi && new Date(data.bitis_tarihi) < new Date()) {
      setCouponError('Bu kuponun süresi dolmuş');
      return;
    }
    if (data.minimum_sepet_tutari && totalPrice < data.minimum_sepet_tutari) {
      setCouponError(`Minimum sepet tutarı ${data.minimum_sepet_tutari}₺`);
      return;
    }
    if (data.kullanim_limiti && data.kullanim_sayisi >= data.kullanim_limiti) {
      setCouponError('Bu kupon kullanım limitine ulaşmış');
      return;
    }

    setCoupon(data as CouponData);
  };

  const removeCoupon = () => {
    setCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  const goToPayment = () => {
    if (validateAddress()) setStep('payment');
  };

  const validatePayment = (): boolean => {
    if (!paymentMethod) {
      setPaymentError('Lütfen bir ödeme yöntemi seçin');
      return false;
    }
    setPaymentError('');
    return true;
  };

  const placeOrder = async (clearCart: () => void) => {
    if (!validatePayment()) return;
    setOrderLoading(true);
    setOrderError('');
    const sipNo = generateSiparisNo();

    const orderPayload = {
      site_id: SITE_ID,
      siparis_no: sipNo,
      musteri_adi: addressData.ad_soyad,
      musteri_email: addressData.email,
      musteri_telefon: addressData.telefon,
      musteri_adres: `${addressData.adres}, ${addressData.ilce}/${addressData.il_adi}`,
      urunler: items.map(item => ({
        urun_id: item.product_id,
        urun_adi: item.name,
        adet: item.quantity,
        birim_fiyat: item.price,
        toplam: item.price * item.quantity,
        gorsel: item.image_url,
      })),
      ara_toplam: totalPrice,
      kargo_ucreti: effectiveShipping,
      indirim: isFreeCargo ? 0 : discount,
      toplam_tutar: grandTotal,
      durum: 'beklemede',
      odeme_durumu: 'beklemede',
      odeme_yontemi: paymentMethod === 'card' ? 'kredi_karti' : paymentMethod === 'bank' ? 'havale_eft' : paymentMethod === 'cash' ? 'kapida_odeme' : 'belirsiz',
      notlar: addressData.notlar || null,
      olusturma_tarihi: nowTR(),
    };

    const { error } = await supabase.from('siparisler').insert(orderPayload);

    if (error) {
      setOrderError('Sipariş kaydedilirken bir hata oluştu. Lütfen tekrar deneyin.');
      setOrderLoading(false);
      return;
    }

    // Kupon kullanım sayısını artır
    if (coupon?.id) {
      await supabase
        .from('kampanyalar')
        .update({ kullanim_sayisi: (coupon as any).kullanim_sayisi + 1 })
        .eq('id', coupon.id);
    }

    setOrderNo(sipNo);
    setOrderLoading(false);
    clearCart();
    setStep('confirm');
  };

  const selectSavedAddress = (addr: SavedAddress) => {
    setSelectedAddressId(addr.id);
    setAddressData(prev => ({
      ...prev,
      ad_soyad: addr.ad_soyad,
      telefon: addr.telefon,
      il_kodu: addr.il_kodu,
      il_adi: addr.il_adi,
      ilce: addr.ilce,
      adres: addr.adres,
    }));
  };

  return {
    step, setStep,
    addressData, setAddressData, addressErrors,
    savedAddresses, selectedAddressId, selectSavedAddress,
    paymentMethod, setPaymentMethod,
    couponCode, setCouponCode,
    coupon, couponError, couponLoading,
    applyCoupon, removeCoupon,
    orderLoading, orderError, orderNo,
    shippingCost: effectiveShipping,
    codFee,
    codLimitExceeded,
    codUpperLimit,
    discount: isFreeCargo ? 0 : discount,
    grandTotal,
    freeShipping,
    paymentError, setPaymentError,
    validatePayment,
    goToPayment,
    placeOrder,
  };
}
